import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface EngagementRow {
  id: string
  artistId: string
  artistName: string
  showId: string | null
  showTitle: string | null
  showDate: string | null
  showCity: string | null
  role: string
  engagementType: string
  startDate: string
  endDate: string
  status: 'offered' | 'accepted' | 'declined' | 'cancelled' | 'completed'
  dayRateMinor: number | null
}

const STATUS_COLOUR: Record<string, string> = {
  offered: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-rose-100 text-rose-700',
  completed: 'bg-slate-100 text-slate-700',
}

export function CrewEngagementsPage() {
  const qc = useQueryClient()
  const { data, error, isLoading } = useQuery<EngagementRow[], ApiClientError>({
    queryKey: ['crew-engagements'],
    queryFn: () => apiFetch<EngagementRow[]>('/crew/profile/engagements'),
  })

  async function respond(engagementId: string, action: 'accept' | 'decline') {
    try {
      await apiFetch(`/crew/profile/engagements/${engagementId}/${action}`, {
        method: 'POST',
      })
      qc.invalidateQueries({ queryKey: ['crew-engagements'] })
      qc.invalidateQueries({ queryKey: ['crew-availability'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    }
  }

  return (
    <div>
      <PageHeader
        title="Engagements"
        subtitle="Offers, accepted gigs, and past shows across every artist you work with."
      />
      <AsyncBoundary isLoading={isLoading} error={error}>
        {data && data.length === 0 ? (
          <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            No engagements yet. Artist teams will send offers via the talent pool.
          </div>
        ) : (
          <div className="space-y-3">
            {data?.map((row) => (
              <div
                key={row.id}
                className="rounded-lg border border-ovation-ink/10 bg-white p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ovation-muted">
                      {row.artistName}
                    </div>
                    <div className="mt-1 font-display text-lg">
                      {row.role.replace(/_/g, ' ')}
                      {row.showTitle && (
                        <span className="text-ovation-muted"> · {row.showTitle}</span>
                      )}
                    </div>
                    <div className="text-sm text-ovation-muted">
                      {new Date(row.startDate).toLocaleDateString()} →{' '}
                      {new Date(row.endDate).toLocaleDateString()}
                      {row.showCity && ` · ${row.showCity}`}
                    </div>
                    {row.dayRateMinor != null && (
                      <div className="mt-1 text-sm">
                        Day rate: £{(row.dayRateMinor / 100).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs ${STATUS_COLOUR[row.status]}`}
                    >
                      {row.status}
                    </span>
                    {row.status === 'offered' && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => respond(row.id, 'accept')}
                          className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                        >
                          <Check className="h-3 w-3" />
                          Accept
                        </button>
                        <button
                          type="button"
                          onClick={() => respond(row.id, 'decline')}
                          className="flex items-center gap-1 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium text-ovation-ink hover:bg-ovation-ink/5"
                        >
                          <X className="h-3 w-3" />
                          Decline
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}
